import Add from '@mui/icons-material/Add'
import Close from '@mui/icons-material/Close'
import {
  alpha,
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import CROPS from '../mp_backend_crops.json'

type FilterType =
  | 'usda_cdl'
  | 'deepcdl'
  | 'tillage'
  | 'planting_date'
  | 'covercrop'
  | 'harvest_date'

type FilterEntry = {
  id: string
  type: FilterType
  year: number
  landUseCode: number
  tillageCode: number
  plantingDateRange: [number, number]
  coverCropValue?: number // 0 = no, 1 = yes
}

type FiltersProps = {
  setFinalFindSql: (sql: string) => void
}

const FILTER_TYPES: Array<{ label: string; value: FilterType }> = [
  { label: 'usda_cdl', value: 'usda_cdl' },
  { label: 'deepcdl', value: 'deepcdl' },
  { label: 'tillage', value: 'tillage' },
  { label: 'planting_date', value: 'planting_date' },
  { label: 'harvest_date', value: 'harvest_date' },
  { label: 'covercrop', value: 'covercrop' },
]

const COVERCROP_OPTIONS = [
  { label: 'no', value: 0 },
  { label: 'yes', value: 1 },
]

const TILLAGE_OPTIONS = [
  { label: 'no-till', value: 0 },
  { label: 'conservation', value: 1 },
  { label: 'conventional', value: 2 },
]

const YEARS = [2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018]

const LAND_USE_OPTIONS = CROPS.filter(crop => typeof crop.CD_ID === 'number').map(crop => ({
  label: crop.MP_NAME,
  value: crop.CD_ID as number,
}))

function createEntry(): FilterEntry {
  const defaultLandUse = LAND_USE_OPTIONS[0]?.value ?? 1
  return {
    id: `${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}`,
    type: 'usda_cdl',
    year: 2024,
    landUseCode: defaultLandUse,
    tillageCode: 0,
    plantingDateRange: [1, 366],
    coverCropValue: 0,
  }
}

export const Filters = ({ setFinalFindSql }: FiltersProps) => {
  const theme = useTheme()
  const [entries, setEntries] = useState<FilterEntry[]>([createEntry()])

  const panelSx = {
    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    borderRadius: 2,
    backgroundColor:
      theme.palette.mode === 'light'
        ? alpha(theme.palette.background.paper, 0.96)
        : alpha(theme.palette.background.default, 0.84),
    boxShadow: theme.shadows[1],
  }

  const selectSx = {
    minWidth: 140,
    backgroundColor:
      theme.palette.mode === 'light'
        ? theme.palette.background.paper
        : alpha(theme.palette.common.white, 0.08),
    color: theme.palette.text.primary,
    borderRadius: 1,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.divider, 0.9),
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '& .MuiSelect-icon': {
      color: theme.palette.text.secondary,
    },
  }

  const menuProps = {
    PaperProps: {
      sx: {
        mt: 0.5,
        border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
        borderRadius: 1.5,
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: theme.shadows[8],
        '& .MuiMenuItem-root': {
          fontSize: theme.typography.body2.fontSize,
        },
        '& .MuiMenuItem-root:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
        },
        '& .Mui-selected': {
          backgroundColor: alpha(theme.palette.primary.main, 0.14),
        },
        '& .Mui-selected:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.2),
        },
      },
    },
  }

  const rangeValueSx = {
    minWidth: 40,
    px: 1,
    py: 0.5,
    borderRadius: 1,
    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    backgroundColor:
      theme.palette.mode === 'light'
        ? alpha(theme.palette.common.black, 0.04)
        : alpha(theme.palette.common.white, 0.08),
    color: theme.palette.text.primary,
    textAlign: 'center',
    fontVariantNumeric: 'tabular-nums',
  }

  const removeButtonSx = {
    color: theme.palette.text.secondary,
    backgroundColor:
      theme.palette.mode === 'light'
        ? alpha(theme.palette.background.default, 0.92)
        : alpha(theme.palette.background.paper, 0.88),
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'light' ? 1 : 0.95)}`,
    boxShadow:
      theme.palette.mode === 'light'
        ? `0 1px 2px ${alpha(theme.palette.common.black, 0.08)}`
        : `0 1px 3px ${alpha(theme.palette.common.black, 0.32)}`,
    '&:hover': {
      color: theme.palette.text.primary,
      backgroundColor:
        theme.palette.mode === 'light'
          ? theme.palette.background.paper
          : alpha(theme.palette.background.paper, 0.98),
      boxShadow:
        theme.palette.mode === 'light'
          ? `0 1px 3px ${alpha(theme.palette.common.black, 0.12)}`
          : `0 2px 6px ${alpha(theme.palette.common.black, 0.38)}`,
    },
    '&.Mui-disabled': {
      color: theme.palette.text.disabled,
      backgroundColor:
        theme.palette.mode === 'light'
          ? alpha(theme.palette.background.default, 0.72)
          : alpha(theme.palette.common.white, 0.14),
      borderColor: alpha(theme.palette.action.disabled, 0.18),
      boxShadow: 'none',
    },
  }

  const filterParts = useMemo(() => {
    return entries
      .map(entry => {
        if (entry.type === 'usda_cdl') {
          return `usda_cdl_${entry.year} = ${entry.landUseCode}`
        }
        if (entry.type === 'deepcdl') {
          return `deepcdl_${entry.year} = ${entry.landUseCode}`
        }
        if (entry.type === 'tillage') {
          return `tillage_${entry.year} = ${entry.tillageCode}`
        }
        if (entry.type === 'planting_date') {
          const [min, max] = entry.plantingDateRange
          return `(planting_date_${entry.year} >= ${min} AND planting_date_${entry.year} <= ${max})`
        }
        if (entry.type === 'harvest_date') {
          const [min, max] = entry.plantingDateRange
          return `(harvest_date_${entry.year} >= ${min} AND harvest_date_${entry.year} <= ${max})`
        }
        if (entry.type === 'covercrop') {
          return `covercrop_${entry.year} = ${entry.coverCropValue}`
        }
        return ''
      })
      .filter(Boolean)
  }, [entries])

  const finalFilterSql = useMemo(() => filterParts.join(' AND '), [filterParts])

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setFinalFindSql(finalFilterSql)
    }, 300)

    return () => {
      window.clearTimeout(timerId)
    }
  }, [finalFilterSql, setFinalFindSql])

  return (
    <Stack spacing={1.25} sx={{ p: 1.5, ...panelSx }}>
      {entries.map(entry => (
        <Stack
          key={entry.id}
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          sx={{ rowGap: 1 }}
        >
          <Select
            size="small"
            value={entry.type}
            MenuProps={menuProps}
            onChange={event => {
              const type = event.target.value as FilterType
              setEntries(current =>
                current.map(item => {
                  if (item.id !== entry.id) return item
                  // Reset type-specific values when changing type
                  if (type === 'covercrop') {
                    return { ...item, type, coverCropValue: 0 }
                  }
                  if (type === 'usda_cdl' || type === 'deepcdl') {
                    return { ...item, type, landUseCode: LAND_USE_OPTIONS[0]?.value ?? 1 }
                  }
                  if (type === 'tillage') {
                    return { ...item, type, tillageCode: 0 }
                  }
                  if (type === 'planting_date' || type === 'harvest_date') {
                    return { ...item, type, plantingDateRange: [1, 366] }
                  }
                  return { ...item, type }
                })
              )
            }}
            sx={selectSx}
          >
            {FILTER_TYPES.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            value={entry.year}
            MenuProps={menuProps}
            onChange={event => {
              const year = Number(event.target.value)
              setEntries(current =>
                current.map(item => (item.id === entry.id ? { ...item, year } : item))
              )
            }}
            sx={{ ...selectSx, minWidth: 110 }}
          >
            {YEARS.map(year => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>

          {entry.type === 'covercrop' ? (
            <Select
              size="small"
              value={entry.coverCropValue}
              MenuProps={menuProps}
              onChange={event => {
                const coverCropValue = Number(event.target.value)
                setEntries(current =>
                  current.map(item => (item.id === entry.id ? { ...item, coverCropValue } : item))
                )
              }}
              sx={{ ...selectSx, minWidth: 120 }}
            >
              {COVERCROP_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          ) : null}

          {(entry.type === 'usda_cdl' || entry.type === 'deepcdl') && (
            <Select
              size="small"
              value={entry.landUseCode}
              MenuProps={menuProps}
              onChange={event => {
                const landUseCode = Number(event.target.value)
                setEntries(current =>
                  current.map(item => (item.id === entry.id ? { ...item, landUseCode } : item))
                )
              }}
              sx={{ ...selectSx, minWidth: 220 }}
            >
              {LAND_USE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          )}

          {entry.type === 'tillage' && (
            <Select
              size="small"
              value={entry.tillageCode}
              MenuProps={menuProps}
              onChange={event => {
                const tillageCode = Number(event.target.value)
                setEntries(current =>
                  current.map(item => (item.id === entry.id ? { ...item, tillageCode } : item))
                )
              }}
              sx={{ ...selectSx, minWidth: 220 }}
            >
              {TILLAGE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          )}

          {(entry.type === 'planting_date' || entry.type === 'harvest_date') && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ width: 300, zIndex: 1 }}>
              <Box component="span" sx={rangeValueSx}>
                {entry.plantingDateRange[0]}
              </Box>
              <Slider
                value={entry.plantingDateRange}
                min={1}
                max={366}
                step={1}
                onChange={(_, value) => {
                  if (!Array.isArray(value)) {
                    return
                  }
                  setEntries(current =>
                    current.map(item =>
                      item.id === entry.id
                        ? { ...item, plantingDateRange: [value[0], value[1]] }
                        : item
                    )
                  )
                }}
                sx={{
                  width: 220,
                  px: 1,
                  color: theme.palette.primary.main,
                  '& .MuiSlider-track': {
                    border: 'none',
                  },
                  '& .MuiSlider-rail': {
                    opacity: theme.palette.mode === 'light' ? 0.35 : 0.5,
                  },
                }}
              />
              <Box component="span" sx={rangeValueSx}>
                {entry.plantingDateRange[1]}
              </Box>
            </Stack>
          )}

          <Tooltip
            title={entries.length > 1 ? 'Remove filter' : 'At least one filter is required'}
            arrow
          >
            <span>
              <IconButton
                size="small"
                aria-label="Remove filter"
                disabled={entries.length === 1}
                onClick={() => {
                  setEntries(current =>
                    current.length > 1 ? current.filter(item => item.id !== entry.id) : current
                  )
                }}
                sx={removeButtonSx}
              >
                <Close fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ))}

      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={() => setEntries(current => [...current, createEntry()])}
        sx={{ alignSelf: 'flex-start', mt: 0.5 }}
      >
        Add filter
      </Button>
    </Stack>
  )
}
